import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { patchCachedRow, restoreCachedRows } from '../../lib/optimistic';
import type { SupabaseClient } from '../../lib/supabase';
import type { BadgeTone } from '../../components/ui';

/**
 * Social Studio: Angel's social content plan.
 *
 * Separate from Nurture Studio, which is email and lifecycle copy. The two
 * only resemble each other from a distance — this one is platform-shaped,
 * scheduled by publish date, and approved by an admin.
 */

export type SocialStatus =
  'idea' | 'drafting' | 'for_approval' | 'approved' | 'scheduled' | 'published';

export const SOCIAL_STATUS_LABELS: Record<SocialStatus, string> = {
  idea: 'Idea',
  drafting: 'Drafting',
  for_approval: 'For Approval',
  approved: 'Approved',
  scheduled: 'Scheduled',
  published: 'Published',
};

/** Board order: left to right is the way a post actually travels. */
export const SOCIAL_STATUS_ORDER: SocialStatus[] = [
  'idea',
  'drafting',
  'for_approval',
  'approved',
  'scheduled',
  'published',
];

export const SOCIAL_STATUS_TONES: Record<SocialStatus, BadgeTone> = {
  idea: 'neutral',
  drafting: 'neutral',
  for_approval: 'warning',
  approved: 'success',
  scheduled: 'brand',
  published: 'success',
};

export interface SocialPost {
  id: string;
  title: string;
  platforms: string[];
  type: string;
  pillar: string;
  caption: string;
  asset_links: string;
  publish_date: string | null;
  owner_id: string | null;
  status: SocialStatus;
  priority: string;
  approved_by: string | null;
  approved_at: string | null;
  /** Who is doing the work, as distinct from the owner who is accountable. */
  assignee_id: string | null;
  notes: string | null;
  /** Wall-clock time of day, paired with publish_date. */
  publish_time: string | null;
  created_at: string;
}

export interface SocialTheme {
  id: string;
  month: string;
  theme: string;
  notes: string;
}

const POST_COLUMNS =
  'id, title, platforms, type, pillar, caption, asset_links, publish_date, publish_time, owner_id, assignee_id, notes, status, priority, approved_by, approved_at, created_at';
const THEME_COLUMNS = 'id, month, theme, notes';

export async function fetchSocialPosts(client: SupabaseClient): Promise<SocialPost[]> {
  const { data, error } = await client
    .from('social_posts')
    .select(POST_COLUMNS)
    .order('publish_date', { ascending: false });
  if (error) {
    throw new Error(error.message);
  }
  return data ?? [];
}

export async function fetchSocialThemes(client: SupabaseClient): Promise<SocialTheme[]> {
  const { data, error } = await client.from('social_themes').select(THEME_COLUMNS).order('month');
  if (error) {
    throw new Error(error.message);
  }
  return data ?? [];
}

export const socialKeys = {
  all: ['social'] as const,
  posts: () => [...socialKeys.all, 'posts'] as const,
  themes: () => [...socialKeys.all, 'themes'] as const,
};

export function useSocialPosts() {
  const { client } = useAuth();
  return useQuery({
    queryKey: socialKeys.posts(),
    queryFn: () => fetchSocialPosts(client),
    retry: false,
  });
}

export function useSocialThemes() {
  const { client } = useAuth();
  return useQuery({
    queryKey: socialKeys.themes(),
    queryFn: () => fetchSocialThemes(client),
    retry: false,
  });
}

export type SocialPostUpdate = Partial<{
  title: string;
  platforms: string[];
  type: string;
  pillar: string;
  caption: string;
  asset_links: string;
  publish_date: string | null;
  publish_time: string | null;
  owner_id: string | null;
  assignee_id: string | null;
  notes: string | null;
  status: SocialStatus;
  priority: string;
}>;

export function useCreateSocialPost() {
  const { client, access } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = access.status === 'approved' ? access.profile.id : null;
  return useMutation({
    mutationFn: async (input: {
      title: string;
      publishDate: string | null;
      /**
       * Set by the LinkedIn Calendar, so a post added there is a LinkedIn post
       * and stays on the tab it was added on. Left out everywhere else, so the
       * insert is exactly what it was and the column keeps its default.
       */
      platforms?: string[];
    }) => {
      const { error } = await client.from('social_posts').insert({
        title: input.title,
        publish_date: input.publishDate,
        created_by: createdBy,
        owner_id: createdBy,
        ...(input.platforms === undefined ? {} : { platforms: input.platforms }),
      });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: socialKeys.all }),
  });
}

export function useUpdateSocialPost() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { postId: string; changes: SocialPostUpdate }) => {
      const { error } = await client
        .from('social_posts')
        .update(input.changes)
        .eq('id', input.postId);
      if (error) {
        throw new Error(error.message);
      }
    },
    // Optimistic: a post dragged into another status lane lands there at once
    // and returns if the write is refused. Approving is admin-only in the
    // database, so a refusal here is a real answer rather than a glitch — the
    // board says which, and the card goes back to the lane it came from.
    onMutate: (input) =>
      patchCachedRow<SocialPost>(queryClient, socialKeys.posts(), input.postId, input.changes),
    onError: (_error, _input, context) => {
      restoreCachedRows(queryClient, socialKeys.posts(), context);
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: socialKeys.all }),
  });
}

export function useDeleteSocialPost() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (postId: string) => {
      const { error } = await client.from('social_posts').delete().eq('id', postId);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: socialKeys.all }),
  });
}

export function useSaveSocialTheme() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id?: string; month: string; theme: string; notes: string }) => {
      const { error } =
        input.id === undefined
          ? await client
              .from('social_themes')
              .insert({ month: input.month, theme: input.theme, notes: input.notes })
          : await client
              .from('social_themes')
              .update({ month: input.month, theme: input.theme, notes: input.notes })
              .eq('id', input.id);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: socialKeys.all }),
  });
}

export const SOCIAL_ERRORS: Record<string, string> = {
  social_approval_requires_admin: 'Only an admin can approve or un-approve a post.',
};

export function socialErrorMessage(raw: string): string {
  const key = Object.keys(SOCIAL_ERRORS).find((code) => raw.includes(code));
  return (key === undefined ? undefined : SOCIAL_ERRORS[key]) ?? 'That could not be saved.';
}

/** Posts grouped by status, for the board. */
export function postsByStatus(posts: SocialPost[]): Map<SocialStatus, SocialPost[]> {
  const grouped = new Map<SocialStatus, SocialPost[]>();
  for (const status of SOCIAL_STATUS_ORDER) {
    grouped.set(status, []);
  }
  for (const post of posts) {
    grouped.get(post.status)?.push(post);
  }
  return grouped;
}

/** Posts on a given day, for the calendar. Undated posts belong to no day. */
export function postsOnDate(posts: SocialPost[], iso: string): SocialPost[] {
  return posts.filter((post) => post.publish_date === iso);
}
